// src/screens/Merchant/CreateFlashSaleScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import './CreateFlashSaleScreen.css';

import backIcon from '../../assets/backarrow-icon.png';
import placeholderImg from '../../assets/placeholder-banner.png';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const toLocalInput = (d) => {
  // Convert Date -> "YYYY-MM-DDTHH:MM" for <input type="datetime-local">
  const pad = (x) => String(x).padStart(2, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const h = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${y}-${m}-${day}T${h}:${mm}`;
};
// Round "now" to the nearest minute (no seconds) for min= attributes
const nowLocal = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  return toLocalInput(d);
};

function CreateFlashSaleScreen({ user }) {
  const navigate = useNavigate();
  const { productId } = useParams();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);

  // discount state as string (free-typing)
  const [percentStr, setPercentStr] = useState('15');

  // date-time state
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');

  const [maxUnits, setMaxUnits] = useState('');
  const [maxPerBuyer, setMaxPerBuyer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // toast
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: '' }), 2200);
  };

  // load product + default times
  useEffect(() => {
    const run = async () => {
      try {
        const res = await api.get(`/products/${productId}`);
        const prod = res.data?.product || res.data;
        setProduct(prod);

        const nowStr = nowLocal();
        const start = new Date(); // now
        const end = new Date(start.getTime() + 3 * 24 * 60 * 60 * 1000); // +3 days
        end.setSeconds(0, 0);

        setStartAt(nowStr);
        setEndAt(toLocalInput(end));
      } catch (err) {
        console.error('❌ Failed to load product:', err);
        showToast('Failed to load product', 'error');
      } finally {
        setLoading(false);
      }
    };
    if (productId) run();
  }, [productId]);

  const basePrice = useMemo(() => {
    if (!product) return null;
    return product?.pricingView?.basePrice ?? product?.price ?? null;
  }, [product]);

  // discount parsing + validation
  const percentNum = Number(percentStr);
  const isNumeric = Number.isFinite(percentNum);
  const isPercentValid = isNumeric && percentNum >= 5 && percentNum <= 90;
  const sliderValue = isNumeric ? clamp(percentNum, 5, 90) : 15;

  // preview (0–100 clamp)
  const discountedPrice = useMemo(() => {
    if (!basePrice) return null;
    const p = isNumeric ? clamp(percentNum, 0, 100) : 0;
    const val = basePrice * (1 - p / 100);
    const rounded = Math.max(0.01, Math.round(val * 100) / 100);
    return rounded;
  }, [basePrice, isNumeric, percentNum]);

  const activeSaleEnds = product?.pricingView?.isOnFlashSale ? product?.pricingView?.saleEndsAt : null;

  // --- Date/time guards ---

  // Ensure start >= now; if end < start, bump end = start + 1h
  const handleStartChange = (v) => {
    if (!v) return setStartAt(v);
    const now = new Date();
    now.setSeconds(0, 0);

    const newStart = new Date(v);
    if (isNaN(newStart.getTime())) {
      setStartAt(v);
      return;
    }

    if (newStart < now) {
      showToast('Start time cannot be in the past', 'error');
      const fixedStr = nowLocal();
      setStartAt(fixedStr);

      // If end earlier than fixed start, bump by +1h
      const endDate = new Date(endAt);
      if (isNaN(endDate.getTime()) || endDate < now) {
        const e = new Date(now.getTime() + 60 * 60 * 1000);
        setEndAt(toLocalInput(e));
      }
      return;
    }

    setStartAt(v);

    // keep end ≥ start (at least 1 hour if it slips)
    const endDate = new Date(endAt);
    if (isNaN(endDate.getTime()) || endDate < newStart) {
      const e = new Date(newStart.getTime() + 60 * 60 * 1000);
      setEndAt(toLocalInput(e));
    }
  };

  // Ensure end ≥ start; if not, bump to start + 1h
  const handleEndChange = (v) => {
    if (!v) return setEndAt(v);
    const newEnd = new Date(v);
    if (isNaN(newEnd.getTime())) {
      setEndAt(v);
      return;
    }
    const startDate = new Date(startAt || nowLocal());
    if (newEnd < startDate) {
      showToast('End must be after the start time (min +1h)', 'error');
      const e = new Date(startDate.getTime() + 60 * 60 * 1000);
      setEndAt(toLocalInput(e));
      return;
    }
    setEndAt(v);
  };

  const onSubmit = async () => {
    if (!basePrice) return showToast('Invalid base price', 'error');

    // final % validation 5–90
    const n = Number(percentStr);
    if (!Number.isFinite(n) || n < 5 || n > 90) {
      return showToast('Discount must be between 5% and 90%', 'error');
    }
    const p = n;

    // time validation: start ≥ now, end ≥ start, duration in [1h, 14d]
    const now = new Date();
    now.setSeconds(0, 0);
    const s = new Date(startAt);
    const e = new Date(endAt);

    if (isNaN(s.getTime()) || s < now) {
      return showToast('Start time cannot be in the past', 'error');
    }
    if (isNaN(e.getTime()) || e < s) {
      return showToast('End must be after the start time', 'error');
    }

    const ms = e - s;
    const oneHour = 60 * 60 * 1000;
    const fourteenDays = 14 * 24 * 60 * 60 * 1000;
    if (ms < oneHour) return showToast('Duration must be ≥ 1 hour', 'error');
    if (ms > fourteenDays) return showToast('Max duration is 14 days', 'error');

    const constraints = {
      maxUnits: maxUnits ? Math.max(0, Number(maxUnits)) : 0,
      maxPerBuyer: maxPerBuyer ? Math.max(0, Number(maxPerBuyer)) : 0
    };

    setSubmitting(true);
    try {
      await api.post('/merchants/me/flash-sales', {
        productId,
        percentOff: p,
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        constraints,
        username: user?.username
      });
      showToast('Flash sale created 🎉', 'success');

setTimeout(() => navigate('/merchant/flash-sale', { replace: true }), 900);

    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to create flash sale';
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled = submitting || loading;

  const formatPi = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return '';
    return x.toFixed(2).replace(/\.00$/, '');
  };

  const minStart = nowLocal();                 // start cannot be in the past
  const minEnd = startAt || minStart;          // end cannot be before start

  return (
    <div className="cfs-page">
      {/* Topbar */}
      <div className="cfs-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="cfs-back"
          onClick={() => navigate(-1)}
        />
        <h2>Create Flash Sale</h2>
      </div>

      {/* Scrollable center */}
      <div className="cfs-scroll">
        {loading ? (
          <div className="cfs-loading">Loading...</div>
        ) : !product ? (
          <div className="cfs-empty">Product not found.</div>
        ) : (
          <>
            {/* Product header */}
            <div className="cfs-product">
              <img
                src={product.media?.[0] || placeholderImg}
                alt={product.name}
                className="cfs-img"
              />
              <div className="cfs-product-info">
                <div className="cfs-title">{product.name}</div>
                <div className="cfs-price-line">
                  <span className="cfs-base">{formatPi(basePrice)} Pi</span>
                  {product?.pricingView?.isOnFlashSale && (
                    <span className="cfs-active-pill">
                      Active sale — ends {new Date(activeSaleEnds).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="cfs-card">
              <div className="cfs-field">
                <label>Discount</label>

                <div className="cfs-discount-row">
                  {/* Slider (bounded 5–90) */}
                  <input
                    type="range"
                    min="5"
                    max="90"
                    step="1"
                    value={sliderValue}
                    onChange={(e) => setPercentStr(String(e.target.value))}
                  />

                  {/* Number input (free typing) */}
                  <input
                    className="cfs-number"
                    type="number"
                    inputMode="numeric"
                    value={percentStr}
                    onChange={(e) => setPercentStr(e.target.value)}
                    onBlur={() => {
                      const n = Number(percentStr);
                      if (!Number.isFinite(n) || n < 5 || n > 90) {
                        showToast('Discount must be between 5% and 90%', 'error');
                        const fixed = String(clamp(Number(percentStr) || 5, 5, 90));
                        setPercentStr(fixed);
                      }
                    }}
                  />
                  <span className="cfs-percent">%</span>
                </div>

                {/* Live validation hint */}
                {!isPercentValid && (
                  <div
                    className="cfs-validate"
                    style={{ color: '#e53935', fontSize: 12, marginTop: 6 }}
                  >
                    Enter a number between 5 and 90.
                  </div>
                )}

                <div className="cfs-preview">
                  <span className="cfs-old">{formatPi(basePrice)} Pi</span>
                  <span className="cfs-arrow">→</span>
                  <span className="cfs-new">{formatPi(discountedPrice)} Pi</span>
                </div>
              </div>

              <div className="cfs-row">
                <div className="cfs-field">
                  <label>Starts</label>
                  <input
                    type="datetime-local"
                    value={startAt}
                    min={minStart}
                    onChange={(e) => handleStartChange(e.target.value)}
                    onBlur={(e) => handleStartChange(e.target.value)}
                  />
                </div>
                <div className="cfs-field">
                  <label>Ends</label>
                  <input
                    type="datetime-local"
                    value={endAt}
                    min={minEnd}
                    onChange={(e) => handleEndChange(e.target.value)}
                    onBlur={(e) => handleEndChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="cfs-row">
                <div className="cfs-field">
                  <label>Max Units (optional)</label>
                  <input
                    type="number"
                    placeholder="0 = unlimited"
                    value={maxUnits}
                    onChange={(e) => setMaxUnits(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="cfs-field">
                  <label>Max per Buyer (optional)</label>
                  <input
                    type="number"
                    placeholder="0 = unlimited"
                    value={maxPerBuyer}
                    onChange={(e) => setMaxPerBuyer(e.target.value)}
                    min="0"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="cfs-footer">
        <button
          className="cfs-submit"
          disabled={isSubmitDisabled}
          onClick={onSubmit}
        >
          {submitting ? 'Creating…' : 'Create Flash Sale'}
        </button>
      </div>

      {/* Toast */}
      {toast.visible && (
        <div className={`cfs-toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default CreateFlashSaleScreen;
